<template>
  <div class="dash" v-loading="loading">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>统计数据</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="head">
      <span class="title">{{ companyName }} · {{ data.monthLabel }} 经营概况</span>
      <span class="muted">数据截至 {{ data.today }}，充电桩记账不计入</span>
      <el-button size="mini" icon="el-icon-refresh" @click="load">刷新</el-button>
    </div>

    <!-- 指标卡 -->
    <div class="tiles">
      <div class="tile">
        <div class="tile-label">入住率</div>
        <div class="tile-value">{{ rooms.occupancyRate }}%</div>
        <div class="tile-sub">已住 {{ rooms.occupied }} · 空房 {{ rooms.vacant }}<span v-if="Number(rooms.paused)"> · 暂停 {{ rooms.paused }}</span></div>
      </div>
      <div class="tile">
        <div class="tile-label">本月收入</div>
        <div class="tile-value">{{ money(month.income) }}</div>
        <div class="tile-sub">新住 {{ month.checkIns }} 笔 · 续费 {{ month.renewals }} 笔</div>
      </div>
      <div class="tile">
        <div class="tile-label">本月支出</div>
        <div class="tile-value">{{ money(Number(month.refund || 0) + Number(month.otherExpense || 0)) }}</div>
        <div class="tile-sub">退押金 {{ money(month.refund) }} · 其他 {{ money(month.otherExpense) }}</div>
      </div>
      <div class="tile">
        <div class="tile-label">本月结余</div>
        <div class="tile-value" :class="{ negative: Number(month.net) < 0 }">{{ money(month.net) }}</div>
        <div class="tile-sub">收入 − 退押金 − 其他支出</div>
      </div>
      <div class="tile">
        <div class="tile-label">当前余额</div>
        <div class="tile-value">{{ money(month.balance) }}</div>
        <div class="tile-sub">历史累计收入 − 全部支出</div>
      </div>
      <div class="tile">
        <div class="tile-label">7 天内到期</div>
        <div class="tile-value">{{ expiring.length }} 间</div>
        <div class="tile-sub">其中已过期 {{ overdueCount }} 间</div>
      </div>
    </div>

    <el-row :gutter="16">
      <el-col :span="16">
        <div class="card">
          <div class="card-title">近 12 个月收支</div>
          <div class="chart" ref="trend"></div>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="card">
          <div class="card-title">近 12 个月新住与续费笔数</div>
          <div class="chart" ref="counts"></div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :span="8">
        <div class="card">
          <div class="card-title">本月收入构成</div>
          <div class="chart" ref="income"></div>
          <div class="empty" v-if="!data.incomeComposition || !data.incomeComposition.length">本月还没有收入</div>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="card">
          <div class="card-title">本月支出构成</div>
          <div class="chart" ref="expense"></div>
          <div class="empty" v-if="!data.expenseComposition || !data.expenseComposition.length">本月还没有支出</div>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="card">
          <div class="card-title">各房型入住情况</div>
          <div class="chart" ref="roomTypes"></div>
        </div>
      </el-col>
    </el-row>

    <div class="card">
      <div class="card-title">7 天内到期和已过期的房间</div>
      <el-table size="small" :data="expiring" border style="width: 100%;" empty-text="7 天内没有到期的房间">
        <el-table-column prop="roomNo" label="房号" width="120"></el-table-column>
        <el-table-column prop="tenantName" label="租客" width="140" :formatter="row => row.tenantName || '-'"></el-table-column>
        <el-table-column prop="checkOut" label="到期日" width="140"></el-table-column>
        <el-table-column label="状态">
          <template slot-scope="scope">
            <span v-if="scope.row.daysLeft < 0" class="overdue">已过期 {{ -scope.row.daysLeft }} 天</span>
            <span v-else-if="scope.row.daysLeft === 0">今天到期</span>
            <span v-else>还剩 {{ scope.row.daysLeft }} 天</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'
import axios from 'axios'

// 分类色按固定顺序取（已用调色板校验脚本检查色盲区分度）；颜色只表示身份，数值一律用文字色
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7']
const TEXT = '#52514e'
const GRID = '#ebebe8'

const axisStyle = {
  axisLine: { lineStyle: { color: GRID } },
  axisTick: { show: false },
  axisLabel: { color: TEXT, fontSize: 11 },
  splitLine: { lineStyle: { color: GRID } }
}

export default {
  name: 'statistics',
  data() {
    return {
      loading: false,
      data: {},
      charts: []
    }
  },
  computed: {
    companyName() {
      try { return JSON.parse(localStorage.getItem('userdata') || '{}').companyName || '' } catch (e) { return '' }
    },
    rooms() { return this.data.rooms || {} },
    month() { return this.data.month || {} },
    expiring() { return this.data.expiring || [] },
    overdueCount() { return this.expiring.filter(r => r.daysLeft < 0).length }
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    this.charts.forEach(c => c.dispose())
  },
  methods: {
    money(v) {
      const n = Number(v || 0)
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    load() {
      this.loading = true
      axios.get('/api/statistics/dashboard', { headers: { Authorization: 'Bearer ' + localStorage.getItem('logintoken') } })
        .then(res => {
          this.loading = false
          if (res.data.code !== '200') return this.$message({ type: 'info', message: res.data.message })
          this.data = res.data.data
          this.$nextTick(this.render)
        })
        .catch(() => {
          this.loading = false
          this.$message.error('统计数据加载失败，请稍后再试')
        })
    },
    chart(ref) {
      const el = this.$refs[ref]
      let c = echarts.getInstanceByDom(el)
      if (!c) {
        c = echarts.init(el)
        this.charts.push(c)
      }
      return c
    },
    resize() {
      this.charts.forEach(c => c.resize())
    },
    render() {
      const trend = this.data.trend || []
      const months = trend.map(t => t.month.slice(2).replace('-', '/'))
      const tooltipMoney = params => params[0].axisValue + '<br/>' + params
        .map(p => p.marker + p.seriesName + '：' + this.money(p.value)).join('<br/>')

      // 收入、支出（退押金 + 其他堆叠）同一个金额轴；结余用线
      this.chart('trend').setOption({
        color: [SERIES[0], SERIES[1], SERIES[3], SERIES[2]],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: tooltipMoney },
        legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: TEXT } },
        grid: { left: 60, right: 10, top: 36, bottom: 24 },
        xAxis: Object.assign({ type: 'category', data: months }, axisStyle, { splitLine: { show: false } }),
        yAxis: Object.assign({ type: 'value' }, axisStyle),
        series: [
          { name: '收入', type: 'bar', barMaxWidth: 16, barGap: '15%', itemStyle: { barBorderRadius: [4, 4, 0, 0] }, data: trend.map(t => Number(t.income)) },
          { name: '退押金', type: 'bar', stack: 'out', barMaxWidth: 16, itemStyle: { borderColor: '#fff', borderWidth: 1 }, data: trend.map(t => Number(t.refund)) },
          { name: '其他支出', type: 'bar', stack: 'out', barMaxWidth: 16, itemStyle: { barBorderRadius: [4, 4, 0, 0], borderColor: '#fff', borderWidth: 1 }, data: trend.map(t => Number(t.otherExpense)) },
          { name: '结余', type: 'line', symbolSize: 6, lineStyle: { width: 2 }, data: trend.map(t => Number(t.income) - Number(t.refund) - Number(t.otherExpense)) }
        ]
      })

      this.chart('counts').setOption({
        color: [SERIES[0], SERIES[1]],
        tooltip: { trigger: 'axis' },
        legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: TEXT } },
        grid: { left: 36, right: 10, top: 36, bottom: 24 },
        xAxis: Object.assign({ type: 'category', data: months, boundaryGap: false }, axisStyle, { splitLine: { show: false } }),
        yAxis: Object.assign({ type: 'value', minInterval: 1 }, axisStyle),
        series: [
          { name: '新住', type: 'line', symbolSize: 6, lineStyle: { width: 2 }, data: trend.map(t => Number(t.checkIns)) },
          { name: '续费', type: 'line', symbolSize: 6, lineStyle: { width: 2 }, data: trend.map(t => Number(t.renewals)) }
        ]
      })

      const donut = (ref, parts) => this.chart(ref).setOption({
        color: SERIES,
        tooltip: { trigger: 'item', formatter: p => p.marker + p.name + '：' + this.money(p.value) + '（' + p.percent + '%）' },
        legend: { bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: TEXT } },
        series: [{
          type: 'pie',
          radius: ['42%', '66%'],
          center: ['50%', '44%'],
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { color: TEXT, formatter: '{b}\n{d}%' },
          data: (parts || []).map(p => ({ name: p.name, value: Number(p.value) }))
        }]
      }, true)
      donut('income', this.data.incomeComposition)
      donut('expense', this.data.expenseComposition)

      const types = this.data.roomTypes || []
      this.chart('roomTypes').setOption({
        color: [SERIES[0], SERIES[2], '#b5b3ad'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { top: 0, right: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: TEXT } },
        grid: { left: 80, right: 16, top: 36, bottom: 24 },
        xAxis: Object.assign({ type: 'value', minInterval: 1 }, axisStyle),
        yAxis: Object.assign({ type: 'category', data: types.map(t => t.name) }, axisStyle, { splitLine: { show: false } }),
        series: [
          { name: '已住', type: 'bar', stack: 'r', barMaxWidth: 14, itemStyle: { borderColor: '#fff', borderWidth: 1 }, data: types.map(t => Number(t.occupied)) },
          { name: '空房', type: 'bar', stack: 'r', barMaxWidth: 14, itemStyle: { borderColor: '#fff', borderWidth: 1 }, data: types.map(t => Number(t.vacant)) },
          { name: '暂停', type: 'bar', stack: 'r', barMaxWidth: 14, itemStyle: { barBorderRadius: [0, 4, 4, 0], borderColor: '#fff', borderWidth: 1 }, data: types.map(t => Number(t.paused)) }
        ]
      })
    }
  }
}
</script>

<style scoped>
.dash {
  padding-bottom: 20px;
}
.head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 16px 0 12px;
}
.head .title {
  font-size: 16px;
  font-weight: 600;
  color: #0b0b0b;
}
.muted {
  color: #909399;
  font-size: 12px;
  flex: 1;
}
.tiles {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.tile {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 14px 16px;
}
.tile-label {
  color: #606266;
  font-size: 13px;
}
.tile-value {
  font-size: 24px;
  font-weight: 600;
  color: #0b0b0b;
  margin: 6px 0 4px;
  font-variant-numeric: tabular-nums;
}
.tile-value.negative {
  color: #c0392b;
}
.tile-sub {
  color: #909399;
  font-size: 12px;
}
.card {
  position: relative;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}
.chart {
  height: 300px;
}
.empty {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  text-align: center;
  color: #909399;
}
.overdue {
  color: #c0392b;
}
@media (max-width: 1200px) {
  .tiles {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/charts/statistics.vue